import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getMindMapSharing,
  removeMindMapMember,
  revokeMindMapInvitation,
  UpgradeRequiredError,
  updateMindMapLinkAccess,
  upsertMindMapMember,
} from '../../utils/mindmap/apiClient';
import UpgradeDialog from '../ui/UpgradeDialog';
import AiShareSection from './AiShareSection';

export function buildShareUrl(mindmapId, shareToken = null, focusNodeId = null) {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('map', mindmapId);
  if (focusNodeId) url.searchParams.set('node', focusNodeId);
  url.hash = shareToken
    ? new URLSearchParams({ share: shareToken }).toString()
    : '';
  return url.toString();
}

async function copyText(value) {
  if (!value) return false;
  if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
    return false;
  }
  await navigator.clipboard.writeText(value);
  return true;
}

const ROLE_LABELS = {
  viewer: 'Viewer',
  editor: 'Editor',
};

const LINK_OPTIONS = [
  ['restricted', 'Restricted'],
  ['viewer', 'Anyone with the link can view'],
  ['editor', 'Anyone with the link can edit · Pro'],
];

function AccordionSection({ id, title, open, onOpen, isDarkMode, children }) {
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) contentRef.current.inert = !open;
  }, [open]);

  return (
    <section style={{ borderTop: `1px solid ${isDarkMode ? '#3f3f46' : '#e5e7eb'}` }}>
      <h3 style={{ margin: 0 }}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`${id}-content`}
          onClick={onOpen}
          style={{
            width: '100%',
            border: 0,
            padding: '14px 2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 16,
            fontWeight: 700,
            textAlign: 'left',
          }}
        >
          <span>{title}</span>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              fontSize: 19,
              opacity: 0.65,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 220ms ease',
            }}
          >
            ⌄
          </span>
        </button>
      </h3>
      <div
        id={`${id}-content`}
        ref={contentRef}
        aria-hidden={!open}
        style={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          opacity: open ? 1 : 0,
          transition: 'grid-template-rows 260ms ease, opacity 200ms ease',
        }}
      >
        <div style={{ minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0 2px 16px' }}>{children}</div>
        </div>
      </div>
    </section>
  );
}

export default function ShareDialog({
  isOpen,
  onClose,
  mindmapId,
  focusNodeId = null,
  focusNodeLabel = null,
  canManage,
  isDarkMode,
  showNotification,
}) {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [linkAccess, setLinkAccess] = useState('restricted');
  const [currentShareUrl, setCurrentShareUrl] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeRequest, setUpgradeRequest] = useState(null);
  const [activeSection, setActiveSection] = useState('people');

  const notify = useCallback((message, variant = 'info') => {
    if (typeof showNotification === 'function') {
      showNotification(message, variant);
    }
  }, [showNotification]);

  useEffect(() => {
    if (!isOpen || !canManage || !mindmapId) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setActiveSection('people');
    getMindMapSharing(mindmapId)
      .then((settings) => {
        if (cancelled) return;
        setMembers(settings.members || []);
        setInvitations(settings.invitations || []);
        setLinkAccess(settings.linkAccess || 'restricted');
        setCurrentShareUrl(null);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canManage, isOpen, mindmapId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const panelStyle = {
    width: 'min(560px, calc(100vw - 32px))',
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    borderRadius: 16,
    padding: 22,
    background: isDarkMode ? '#242424' : '#ffffff',
    color: isDarkMode ? '#f9fafb' : '#111827',
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.38)',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };
  const fieldStyle = {
    border: `1px solid ${isDarkMode ? '#52525b' : '#d1d5db'}`,
    borderRadius: 9,
    padding: '9px 10px',
    background: isDarkMode ? '#18181b' : '#ffffff',
    color: 'inherit',
    font: 'inherit',
  };
  const buttonStyle = {
    border: 0,
    borderRadius: 999,
    padding: '9px 14px',
    background: '#22c55e',
    color: '#052e16',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const quietButtonStyle = {
    ...buttonStyle,
    background: isDarkMode ? '#3f3f46' : '#e5e7eb',
    color: isDarkMode ? '#f9fafb' : '#111827',
  };

  const handleCopy = async (url) => {
    try {
      if (await copyText(url)) {
        notify('Map link copied to clipboard.', 'success');
      } else {
        notify(`Map link: ${url}`, 'info');
      }
    } catch {
      notify(`Map link: ${url}`, 'info');
    }
  };

  const handleMemberSubmit = async (event) => {
    event?.preventDefault();
    if (!email.trim() || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      const result = await upsertMindMapMember(
        mindmapId,
        email.trim().toLowerCase(),
        role,
        focusNodeId
      );
      if (result.status === 'invitation_sent') {
        setInvitations((previous) => [
          ...previous.filter((item) => item.email !== result.email),
          result,
        ].sort((left, right) => left.email.localeCompare(right.email)));
        notify(`Invitation sent to ${result.email}.`, 'success');
      } else {
        setInvitations((previous) => previous.filter((item) => item.email !== result.email));
        setMembers((previous) => [
          ...previous.filter((item) => item.userId !== result.userId),
          result,
        ].sort((left, right) => left.email.localeCompare(right.email)));
        notify(`${result.email} can now ${role === 'editor' ? 'edit' : 'view'} this map.`, 'success');
      }
      setEmail('');
    } catch (saveError) {
      if (saveError instanceof UpgradeRequiredError) {
        setUpgradeRequest({
          capabilityKey: saveError.upgradeContext.capabilityKey,
          returnLabel: `Add ${email.trim().toLowerCase()} as editor`,
          retry: () => handleMemberSubmit(null),
        });
      }
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMemberRole = async (member, nextRole) => {
    setIsSaving(true);
    setError(null);
    try {
      const updated = await upsertMindMapMember(mindmapId, member.email, nextRole);
      setMembers((previous) => previous.map((item) => (
        item.userId === updated.userId ? updated : item
      )));
      notify(`${updated.email} is now a ${ROLE_LABELS[nextRole].toLowerCase()}.`, 'success');
    } catch (saveError) {
      if (saveError instanceof UpgradeRequiredError) {
        setUpgradeRequest({
          capabilityKey: saveError.upgradeContext.capabilityKey,
          returnLabel: `Make ${member.email} an editor`,
          retry: () => handleMemberRole(member, nextRole),
        });
      }
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveMember = async (member) => {
    setIsSaving(true);
    setError(null);
    try {
      await removeMindMapMember(mindmapId, member.userId);
      setMembers((previous) => previous.filter((item) => item.userId !== member.userId));
      notify(`${member.email} no longer has named access.`, 'success');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevokeInvitation = async (invitation) => {
    setIsSaving(true);
    setError(null);
    try {
      await revokeMindMapInvitation(mindmapId, invitation.invitationId);
      setInvitations((previous) => previous.filter((item) => (
        item.invitationId !== invitation.invitationId
      )));
      notify(`Invitation to ${invitation.email} revoked.`, 'success');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const changeLinkAccess = async (nextAccess, rotate = false) => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await updateMindMapLinkAccess(
        mindmapId,
        nextAccess,
        rotate
      );
      setLinkAccess(result.linkAccess);
      if (result.shareToken) {
        const url = buildShareUrl(mindmapId, result.shareToken, focusNodeId);
        setCurrentShareUrl(url);
        await handleCopy(url);
      } else if (result.linkAccess === 'restricted') {
        setCurrentShareUrl(null);
        notify('Link access disabled.', 'success');
      } else {
        notify(`Existing link access changed to ${ROLE_LABELS[result.linkAccess].toLowerCase()}.`, 'success');
      }
    } catch (saveError) {
      if (saveError instanceof UpgradeRequiredError) {
        setUpgradeRequest({
          capabilityKey: saveError.upgradeContext.capabilityKey,
          returnLabel: 'Enable editor link',
          retry: () => changeLinkAccess(nextAccess, rotate),
        });
      }
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const ordinaryMapUrl = buildShareUrl(mindmapId, null, focusNodeId);
  const sharingBranch = Boolean(focusNodeId);

  return (
    <>
      <div
        role="presentation"
        onMouseDown={(event) => {
          event.stopPropagation();
          if (event.target === event.currentTarget) onClose();
        }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          background: 'rgba(0, 0, 0, 0.48)',
        }}
      >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-dialog-title"
        style={panelStyle}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <h2 id="share-dialog-title" style={{ margin: 0, fontSize: 22 }}>
              {sharingBranch ? 'Share idea' : 'Share map'}
            </h2>
            <p style={{ margin: '5px 0 18px', opacity: 0.75, fontSize: 13 }}>
              {sharingBranch
                ? `Recipients open ${focusNodeLabel ? `“${focusNodeLabel}”` : 'this idea'} as a focused branch. Access still applies to the whole map.`
                : 'Link recipients sign in so edits and presence remain attributable.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close sharing dialog" style={quietButtonStyle}>×</button>
        </div>

        {!canManage ? (
          <div>
            <p>
              Only the map owner can change access. This link works for people who already have permission.
            </p>
            <button type="button" onClick={() => handleCopy(ordinaryMapUrl)} style={buttonStyle}>
              {sharingBranch ? 'Copy idea link' : 'Copy map link'}
            </button>
          </div>
        ) : isLoading ? (
          <p role="status">Loading sharing settings…</p>
        ) : (
          <div>
            <AccordionSection
              id="share-people"
              title="People with access"
              open={activeSection === 'people'}
              onOpen={() => setActiveSection('people')}
              isDarkMode={isDarkMode}
            >
            <form onSubmit={handleMemberSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: 8 }}>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  aria-label="Collaborator email"
                  style={{ ...fieldStyle, minWidth: 0 }}
                  required
                />
                <select value={role} onChange={(event) => setRole(event.target.value)} aria-label="Collaborator role" style={fieldStyle}>
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor · Pro after the first editor</option>
                </select>
                <button type="submit" disabled={isSaving} style={buttonStyle}>Add</button>
              </div>
              <p style={{ margin: '7px 0 14px', fontSize: 12, opacity: 0.72 }}>
                New users receive a seven-day invitation and can sign up from the email.
              </p>
            </form>

            {invitations.length > 0 && (
              <div style={{ display: 'grid', gap: 8, marginBottom: 18 }}>
                {invitations.map((invitation) => (
                  <div
                    key={invitation.invitationId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {invitation.email}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>Invitation pending</div>
                    </div>
                    <span style={{ fontSize: 13 }}>{ROLE_LABELS[invitation.role]}</span>
                    <button
                      type="button"
                      onClick={() => handleRevokeInvitation(invitation)}
                      disabled={isSaving}
                      style={quietButtonStyle}
                    >
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            )}

            {members.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                {members.map((member) => (
                  <div key={member.userId} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: 8, alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {member.displayName || member.email}
                      </div>
                      {member.displayName && <div style={{ fontSize: 12, opacity: 0.7 }}>{member.email}</div>}
                    </div>
                    <select
                      value={member.role}
                      onChange={(event) => handleMemberRole(member, event.target.value)}
                      disabled={isSaving}
                      aria-label={`Role for ${member.email}`}
                      style={fieldStyle}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor · Pro after the first editor</option>
                    </select>
                    <button type="button" onClick={() => handleRemoveMember(member)} disabled={isSaving} style={quietButtonStyle}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            </AccordionSection>

            <AccordionSection
              id="share-general"
              title="General access"
              open={activeSection === 'general'}
              onOpen={() => setActiveSection('general')}
              isDarkMode={isDarkMode}
            >
              <select
                value={linkAccess}
                onChange={(event) => changeLinkAccess(event.target.value)}
                disabled={isSaving}
                aria-label="General link access"
                style={{ ...fieldStyle, width: '100%' }}
              >
                {LINK_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {linkAccess === 'restricted' ? (
                  <button type="button" onClick={() => handleCopy(ordinaryMapUrl)} style={quietButtonStyle}>
                    Copy link for named people
                  </button>
                ) : currentShareUrl ? (
                  <button type="button" onClick={() => handleCopy(currentShareUrl)} style={buttonStyle}>
                    Copy anyone link
                  </button>
                ) : (
                  <button type="button" onClick={() => changeLinkAccess(linkAccess, true)} disabled={isSaving} style={buttonStyle}>
                    Replace and copy link
                  </button>
                )}
              </div>
              {linkAccess !== 'restricted' && !currentShareUrl && (
                <p style={{ margin: '8px 0 0', fontSize: 12, opacity: 0.72 }}>
                  The existing secret cannot be retrieved. Replacing it revokes the previous link immediately.
                </p>
              )}
            </AccordionSection>

            <AccordionSection
                id="share-ai"
                title="Share with AI"
                open={activeSection === 'ai'}
                onOpen={() => setActiveSection('ai')}
                isDarkMode={isDarkMode}
              >
                <AiShareSection
                  key={`${mindmapId}:${focusNodeId || "whole-map"}`}
                  mindmapId={mindmapId}
                  focusNodeId={focusNodeId}
                  focusNodeLabel={focusNodeLabel}
                  isDarkMode={isDarkMode}
                  showNotification={showNotification}
                  buttonStyle={buttonStyle}
                  quietButtonStyle={quietButtonStyle}
                  embedded
                />
              </AccordionSection>
          </div>
        )}

        {error && <p role="alert" style={{ margin: '16px 0 0', color: '#ef4444', fontWeight: 700 }}>{error}</p>}
      </section>
      </div>
      <UpgradeDialog
        isOpen={Boolean(upgradeRequest)}
        capabilityKey={upgradeRequest?.capabilityKey}
        returnLabel={upgradeRequest?.returnLabel}
        isDarkMode={isDarkMode}
        onClose={() => setUpgradeRequest(null)}
        onActivated={async () => {
          const retry = upgradeRequest?.retry;
          setUpgradeRequest(null);
          if (retry) await retry();
        }}
      />
    </>
  );
}
